import { css } from "@emotion/react"
import { Section, SectionType } from "Components/Sections/sections"
import { FC, JSX, useContext, useEffect, useRef, useState } from "react"
import { transition, transitionTime } from "theme/shared"
import { GlobalContext } from "./GlobalContext"
import { SectionProps } from "./SectionProps"

export const TRANSITION_CLASS = "transitionClass"

type ProjectsCssTransitionProps = SectionProps & {
  enableSectionIn?: boolean
  activeSection: (section: Section, transitionClassName: string) => JSX.Element
  sections: SectionType[]
}

const ProjectsCssTransition: FC<ProjectsCssTransitionProps> = ({
  activeSection,
  position,
  sections = [],
}) => {
  const { active } = useContext(GlobalContext)
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set([active])
  )
  const [animatingOut, setAnimatingOut] = useState<Set<number>>(new Set())
  const [animatingIn, setAnimatingIn] = useState<Set<number>>(new Set())
  const [enteringActive, setEnteringActive] = useState<Set<number>>(new Set())
  const prevActiveRef = useRef(active)
  const activeRef = useRef(active)
  const timersRef = useRef<Map<number, NodeJS.Timeout>>(new Map())

  // Keep activeRef in sync
  useEffect(() => {
    activeRef.current = active
  }, [active])

  useEffect(() => {
    if (prevActiveRef.current !== active) {
      // Capture the section to remove BEFORE updating the ref
      const sectionToRemove = prevActiveRef.current

      // Add new active section to visible set
      setVisibleSections((prev) => {
        const next = new Set(prev)
        next.add(active)
        return next
      })

      // Mark new section as animating in (enter class), old section as animating out
      setAnimatingIn(new Set([active]))
      setAnimatingOut(new Set([sectionToRemove]))
      setEnteringActive(new Set()) // Clear entering-active initially

      // Trigger enter-active class after a tick to enable transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEnteringActive(new Set([active]))
        })
      })

      // Update ref immediately
      prevActiveRef.current = active

      // Remove old section after transition completes
      const timer = setTimeout(() => {
        // Check if the section we're about to remove is currently active
        // (e.g. user navigated away and back quickly)
        if (sectionToRemove === activeRef.current) {
          // We still need to clear the animatingOut state for it
          setAnimatingOut((prev) => {
            const next = new Set(prev)
            next.delete(sectionToRemove)
            return next
          })
          return
        }

        setVisibleSections((prev) => {
          const next = new Set(prev)
          next.delete(sectionToRemove)
          return next
        })
        setAnimatingOut(new Set())
        setAnimatingIn(new Set())
        setEnteringActive(new Set())
        timersRef.current.delete(sectionToRemove)
      }, transitionTime)

      timersRef.current.set(sectionToRemove, timer)
    }
  }, [active]) // Only depend on active

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer))
      timersRef.current.clear()
    }
  }, [])

  return (
    <>
      {sections.map((section, index) => {
        if (index < position) {
          return null
        }

        const isVisible = visibleSections.has(index)
        const isActive = active === index
        const isExiting = animatingOut.has(index)
        const isEntering = animatingIn.has(index)
        const isEnteringActive = enteringActive.has(index)

        if (!isVisible) {
          return null
        }

        // Build className based on transition state
        let className = ""
        if (isEntering && isEnteringActive) {
          className = `${TRANSITION_CLASS}-enter ${TRANSITION_CLASS}-enter-active`
        } else if (isEntering) {
          className = `${TRANSITION_CLASS}-enter`
        } else if (isExiting) {
          className = `${TRANSITION_CLASS}-exit ${TRANSITION_CLASS}-exit-active`
        }

        return <div key={index}>{activeSection(section, className)}</div>
      })}
    </>
  )
}

export default ProjectsCssTransition

export const fadeTransition = css`
  &.${TRANSITION_CLASS}-enter {
    opacity: 0;
  }

  &.${TRANSITION_CLASS}-enter-active {
    opacity: 1;
    ${transition};
  }

  &.${TRANSITION_CLASS}-exit {
    opacity: 1;
  }

  &.${TRANSITION_CLASS}-exit-active {
    opacity: 0;
    ${transition};
  }
`
